import { z } from "zod";
import type { ConfigTypeId } from "../registry";
import { defineStepConfig } from "../registry";

/**
 * Stable type identifier for the HTTP trigger step.
 *
 * Imported by both backend orchestration code (to detect entry steps that
 * should be auto-completed) and external HTTP trigger validation code (to
 * find the trigger node in a pinned workflow definition).
 */
export const HTTP_TRIGGER_STEP_TYPE = "http_trigger" satisfies ConfigTypeId;

const httpTriggerConfigSchema = z.object({
  /** Schema definition whose published version validates the inbound POST body. */
  bodySchemaDefinitionId: z
    .string()
    .uuid()
    .describe("Stored schema definition (same catalog as extraction schemas)."),
  /** Pinned immutable version — `snapshot.fields` is the body contract. */
  bodySchemaDefinitionVersionId: z
    .string()
    .uuid()
    .describe(
      "Published schema version whose snapshot drives validation and output sockets.",
    ),
});

export const httpTriggerStep = defineStepConfig({
  type: HTTP_TRIGGER_STEP_TYPE,
  name: "HTTP / API Trigger",
  description:
    "Entry point for workflows triggered by an external HTTP API call using a lineage-bound API token.",
  category: "Trigger",

  inputSchema: z.never(),

  // Output sockets are dynamic: one per root field in the pinned schema plus
  // `payload`. See `schemaRootFieldsToHttpTriggerOutputSockets` in `@erp/workflows`.
  outputSchema: z.object({
    payload: z.record(z.string(), z.unknown()),
  }),

  configSchema: httpTriggerConfigSchema,
  emits: [] as const,
});

export type HttpTriggerConfig = z.infer<typeof httpTriggerStep.configSchema>;
